import React, { useMemo, useState } from 'react';
import { AlertCircle, Banknote, CalendarDays, ChevronLeft, ChevronRight, FileStack, Layers, Presentation, Trophy, UserPlus, Users } from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useTrendsReport } from '../../../hooks/pipelineTracker/useTrendsReport';
import { usePipelineReport, formatCents, formatIsoDate } from '../../../hooks/pipelineTracker/usePipelineReport';
import { useLeadSourceReport } from '../../../hooks/pipelineTracker/useLeadSourceReport';
import { useBrokerMonthlyReport } from '../../../hooks/pipelineTracker/useBrokerMonthlyReport';
import { useSalesAnalyticsReport } from '../../../hooks/pipelineTracker/useSalesAnalyticsReport';
import { usePipelineTargets } from '../../../hooks/pipelineTracker/usePipelineTargets';
import { averageDealTarget } from '../../../hooks/pipelineTracker/targetsContract';
import {
  defaultPackMonth,
  lastDayOfMonth,
  monthLabel,
  packFlowWindow,
  packMonthsFromSnapshots,
  flowOnlyPackMonth,
  packWindow,
  previousMonth,
} from '../../../hooks/pipelineTracker/remContract';
import { ComingSoonList, Honesty, Panel, num, type ComingSoonItem } from './pipelineParts';
import { CHART_PALETTE, Chip, HousePanel, RatioBar } from './trackerKit';
import { BRAND_BLUE } from './trackerUi';
import {
  buildPackBullets,
  buildPackComparisons,
  dollars,
  monthShortYear,
  sameMonthLastYear,
  topSettlers,
  type Baseline,
  type PackComparison,
  type PackComparisonInput,
} from './boardPackBullets';
import type { BrokerMonthlyResponse, PackMonth } from '../../../hooks/pipelineTracker/remContract';
import type { TrendPoint } from '../../../hooks/pipelineTracker/useTrendsReport';
import { ComparisonCard, LeaderCard } from './BoardPackCards';
import { ForecastPanel, PipelineVelocityPanel, PlannedSettlementsPanel, SalesAnalyticsState } from './SalesAnalyticsSections';
import { TrackerSection } from './TrackerSection';
import { NetBookPanel } from './NetBookPanel';
import { TargetGradeLine } from './targetGradeLine';

/**
 * Board pack: one on-screen page for one month, composed from reports the tracker already
 * serves. Nothing on it is typed in, and nothing is estimated.
 *
 * A FINISHED MONTH READS AS A WHOLE MONTH. Practices upload weekly, so a month's last export is
 * rarely on its last day. The pack reads each kind of figure where it is true (remContract.ts
 * PackMonth): flow (settled, submitted, the broker table, referral leads) from the first export
 * dated after the month ended, over the 1st to the last day; stock (pipeline value, deals in
 * flight) from the last export on or before month end, whose date the pack states, because a
 * photograph of the book cannot be backdated. Until an export after the month exists, every figure
 * comes from the month's last export, over the 1st to its date, and the month is a part month.
 *
 * LIKE WITH LIKE. "What changed" compares a complete month only with a complete previous month,
 * itself read from ITS own first export after it ended, never from this month's export. A part
 * month is never compared with a whole one. The same holds for the same month last year (founder,
 * 5 Oct 2026): read from that month's own first later export when the history reaches back that
 * far, and otherwise named as not available ("No Aug 2025 export"), never estimated.
 *
 * The forecast and planned settlements are the Pipeline tab's own sections (phase 3), asked of the
 * month-end export, so the pack and the tab can never disagree about the months ahead.
 *
 * NOT IN THIS BUILD: a Managing Director's note, scheduled publishing, share links and emailing.
 * The pack says so in one line rather than showing controls that do nothing.
 *
 * NET SETTLEMENTS is the loan book from the lender commission statements, not Salestrekker: the
 * month-end book and its change over the month, as the dashboard's Loan Book serves it (netBook.ts).
 *
 * TARGETS (brief gaps, 2026-10-20). The tenant's targets document is loaded once here and passed to
 * the brokers table (average settled deal against each broker's own target, the Total row against
 * the company's) and to the net settlements panel. Null until loaded, so no line says "No target
 * set" before the answer arrives; a document that could not be loaded grades nothing.
 *
 * WAITING SECTIONS are not drawn as cards. Team utilisation is named in the pack's "Coming when the
 * data is available" list, with what it needs and who owes it (founder, 1 Oct 2026), and shows no
 * figure until its definition is agreed.
 */

/** The pack's sections that cannot be shown yet. */
export const BOARD_PACK_COMING_SOON: ReadonlyArray<ComingSoonItem> = [
  {
    section: 'Team utilisation',
    from: 'Christian Paterson (Auctus)',
    needs: 'The capacity measure: how loaded each broker is against their capacity.',
  },
];

const TH = 'px-2.5 py-2 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-gray-400';
const TD = 'px-2.5 py-2 tabular-nums';
/** Zebra rows with a hover lift, as the File quality tables. */
const ROW = 'border-b border-gray-800/80 transition-colors odd:bg-white/[0.015] hover:bg-white/[0.04]';

/** Accent per headline figure, from the house chart palette: settled emerald, submissions blue, pipeline violet. */
const COMPARE_STYLE: Record<PackComparison['key'], { color: string; icon: typeof Banknote }> = {
  settlements: { color: CHART_PALETTE[2], icon: Banknote },
  submissions: { color: CHART_PALETTE[0], icon: FileStack },
  pipeline: { color: CHART_PALETTE[3], icon: Layers },
};

/**
 * A flow baseline (settled and submissions) for the comparison cards, or the reason there is none.
 * Exported for its tests. `pack` is the baseline month's PackMonth, `response` the broker months
 * report asked of that month's own first export after it ended.
 */
export function flowBaseline({ month, complete, pack, response, error }: {
  month: string;
  complete: boolean;
  pack: PackMonth | null;
  response: BrokerMonthlyResponse | null;
  error: string | null;
}): PackComparisonInput['previousFlow'] {
  const short = monthShortYear(month);
  if (!complete) return { month, reason: 'Part month, not compared' };
  if (!pack) return { month, reason: `No ${short} export` };
  if (!pack.complete) return { month, reason: `${short} is a part month` };
  if (error) return { month, reason: `${short} could not be loaded` };
  if (!response || response.status !== 'ok') return { month, reason: `No ${short} export` };
  // Defence in depth: the baseline's own window must be whole, or it is not compared.
  if (response.months?.find((w) => w.month === month)?.partial === true) return { month, reason: `${short} is a part month` };
  const totals = response.totals.find((t) => t.month === month);
  if (!totals) return { month, reason: `No ${short} figures` };
  return { month, settled: totals.settled, submissions: totals.submissions };
}

/**
 * Settled dollars over settled deals, in whole cents, or null when nothing settled or the settled
 * total is not positive (the export can carry a negative deal value). Exported for its tests.
 */
export function averageDealCents(settled: { count: number; valueCents: number }): number | null {
  if (settled.count <= 0 || settled.valueCents <= 0) return null;
  return Math.round(settled.valueCents / settled.count);
}

/** A stock baseline: the trend series at the baseline month's last export. */
export function stockBaseline(month: string, pack: PackMonth | null, points: readonly TrendPoint[]): Baseline {
  // A flow-only baseline (flowOnlyPackMonth) has no month-end photograph of the book.
  if (pack && pack.asAtDate === '') return { status: 'unavailable', month, reason: `No ${monthShortYear(month)} month-end export` };
  const point = pack ? points.find((p) => p.asAtDate === pack.asAtDate) : undefined;
  if (!point) return { status: 'unavailable', month, reason: `No ${monthShortYear(month)} export` };
  return { status: 'ok', month, flow: { count: point.deals, valueCents: point.loanAmountCents } };
}

/**
 * The line under the pack's title: which export each kind of figure comes from, and over what range.
 * Exported for its tests; the wording is the pack's statement of basis, so it is pinned.
 */
export function packBlurb({ month, asAtDate, flowAsAtDate, complete, range }: {
  month: string;
  asAtDate: string;
  flowAsAtDate: string;
  complete: boolean;
  range: { from: string; to: string };
}): string {
  const covers = `Settled and submitted cover ${formatIsoDate(range.from)} to ${formatIsoDate(range.to)}`;
  const name = monthLabel(month);
  if (complete && flowAsAtDate !== asAtDate) {
    return `${covers}, read from the ${formatIsoDate(flowAsAtDate)} export, the first published after ${name} ended. `
      + `Pipeline value is the book at month end, as at the ${formatIsoDate(asAtDate)} export, the last one on or before ${formatIsoDate(lastDayOfMonth(month))}.`;
  }
  if (complete) {
    return `Every figure is as at the ${formatIsoDate(asAtDate)} export, published on the last day of ${name}. ${covers}.`;
  }
  return `Every figure is as at the ${formatIsoDate(asAtDate)} export, the last one published in ${name}. ${covers}. `
    + `${name} will read as a whole month once an export dated after ${formatIsoDate(lastDayOfMonth(month))} is published.`;
}

const BoardPackTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const trends = useTrendsReport(companyId);
  const { targets } = usePipelineTargets(companyId);
  const points = useMemo(
    () => (trends.result?.status === 'ok' ? trends.result.data.points : []),
    [trends.result],
  );
  const packMonths = useMemo(() => packMonthsFromSnapshots(points.map((p) => p.asAtDate)), [points]);
  const [chosen, setChosen] = useState<string | null>(null);
  // Default: the newest complete month. The running month is in the list but not the default.
  const selected = packMonths.find((m) => m.month === chosen) ?? defaultPackMonth(packMonths);
  // Stock: the month-end photograph. Flow: the export that sees the whole month, when one exists.
  const asAtDate = selected?.asAtDate;
  const flowAsAtDate = selected?.flowAsAtDate;
  const complete = Boolean(selected?.complete);
  const range = selected ? packFlowWindow(selected) : null;
  const stockRange = asAtDate ? packWindow(asAtDate) : null;
  // Until a later export exists both kinds of figure come from one export, asked once.
  const sameExport = flowAsAtDate === asAtDate;

  const pipeline = usePipelineReport({
    companyId: asAtDate ? companyId : '',
    ...(asAtDate ? { asAtDate } : {}),
    ...(stockRange ? { windowStart: stockRange.from, windowEnd: stockRange.to } : {}),
  });
  const flowPipeline = usePipelineReport({
    companyId: flowAsAtDate && !sameExport ? companyId : '',
    ...(flowAsAtDate && !sameExport ? { asAtDate: flowAsAtDate } : {}),
    ...(range && !sameExport ? { windowStart: range.from, windowEnd: range.to } : {}),
  });
  const months = useBrokerMonthlyReport(companyId, { ...(flowAsAtDate ? { asAtDate: flowAsAtDate } : {}), enabled: Boolean(flowAsAtDate) });
  const leads = useLeadSourceReport(
    flowAsAtDate ? companyId : undefined,
    flowAsAtDate,
    range ? { windowStart: range.from, windowEnd: range.to } : {},
  );

  // The previous month, for "what changed": only when both months are complete, and read from the
  // previous month's OWN first export after it ended, so the two figures are on the same basis.
  // That export is this month's flow export only when both are the same date; then it is not asked twice.
  const month = selected?.month ?? null;
  const prevMonth = month ? previousMonth(month) : null;
  // The month before the first export has no PackMonth, but its flow is read from the first export
  // after it ended like any other month's (flowOnlyPackMonth), so it is still compared.
  const prevPack = prevMonth
    ? packMonths.find((m) => m.month === prevMonth) ?? flowOnlyPackMonth(prevMonth, points.map((p) => p.asAtDate))
    : null;
  const comparable = complete && Boolean(prevPack?.complete);
  const prevFlowAsAtDate = comparable ? prevPack?.flowAsAtDate : undefined;
  const prevShared = prevFlowAsAtDate !== undefined && prevFlowAsAtDate === flowAsAtDate;
  // The same month last year, on the same basis: complete, from its own first export after it ended.
  // Read only when the snapshot history reaches back that far; never asked twice for one export.
  const lastYearMonth = month ? sameMonthLastYear(month) : null;
  const lyPack = lastYearMonth ? packMonths.find((m) => m.month === lastYearMonth) ?? null : null;
  const lyFlowAsAtDate = complete && lyPack?.complete ? lyPack.flowAsAtDate : undefined;
  const lySharedWith = lyFlowAsAtDate === undefined ? null
    : lyFlowAsAtDate === flowAsAtDate ? 'this' : lyFlowAsAtDate === prevFlowAsAtDate ? 'prev' : null;
  const lyMonths = useBrokerMonthlyReport(companyId, {
    ...(lyFlowAsAtDate && !lySharedWith ? { asAtDate: lyFlowAsAtDate } : {}),
    enabled: Boolean(lyFlowAsAtDate) && !lySharedWith,
  });
  const prevMonths = useBrokerMonthlyReport(companyId, {
    ...(prevFlowAsAtDate && !prevShared ? { asAtDate: prevFlowAsAtDate } : {}),
    enabled: Boolean(prevFlowAsAtDate) && !prevShared,
  });

  // Forecast and planned settlements, from the month-end export. Both read the deals in flight at
  // the export date; the range only fixes which export and scope are asked.
  const sales = useSalesAnalyticsReport({
    companyId: asAtDate ? companyId : '',
    ...(asAtDate ? { asAtDate } : {}),
    ...(stockRange ? { windowStart: stockRange.from, windowEnd: stockRange.to } : {}),
    enabled: Boolean(asAtDate && stockRange),
  });

  // NEVER A STALE MONTH UNDER A NEW TITLE. Each hook keeps its last answer while it fetches the
  // next, so on switching month the previous month's figures would sit under the new month's
  // heading until the new ones arrived. A report that is loading is therefore read as absent, and
  // each section shows a skeleton for the loading month instead.
  const report = !pipeline.loading && pipeline.response?.status === 'ok' ? pipeline.response : null;
  const flowLoading = sameExport ? pipeline.loading : flowPipeline.loading;
  const flowError = sameExport ? null : flowPipeline.error;
  const flowResponse = sameExport ? pipeline.response : flowPipeline.response;
  const flowReport = sameExport ? report : !flowPipeline.loading && flowPipeline.response?.status === 'ok' ? flowPipeline.response : null;
  const salesData = !sales.loading && sales.response?.status === 'ok' ? sales.response : null;
  const monthly = !months.loading && months.response?.status === 'ok' ? months.response : null;
  const leadResult = leads.loading ? null : leads.result;
  const leadData = leadResult?.status === 'ok' ? leadResult.data : null;
  const prevMonthly = prevShared ? monthly : !prevMonths.loading && prevMonths.response?.status === 'ok' ? prevMonths.response : null;
  const prevError = prevShared ? null : prevMonths.error;
  const lyMonthly = lySharedWith === 'this' ? monthly : lySharedWith === 'prev' ? prevMonthly
    : !lyMonths.loading && lyMonths.response?.status === 'ok' ? lyMonths.response : null;
  const lyError = lySharedWith ? null : lyMonths.error;
  const anyLoading = pipeline.loading || flowLoading || months.loading || leads.loading
    || (!prevShared && prevMonths.loading) || (!lySharedWith && lyMonths.loading);

  const brokerRows = useMemo(() => {
    if (!monthly || !month) return [];
    return monthly.brokers
      .map((b) => {
        const m = b.months.find((x) => x.month === month);
        return { key: b.adviserKey, name: b.adviserName, kind: b.kind ?? 'broker', settled: m?.settled ?? { count: 0, valueCents: 0 }, submissions: m?.submissions ?? { count: 0, valueCents: 0 } };
      })
      .filter((r) => r.settled.count > 0 || r.submissions.count > 0)
      .sort((a, b) => b.settled.valueCents - a.settled.valueCents || b.submissions.count - a.submissions.count || a.name.localeCompare(b.name));
  }, [monthly, month]);
  const monthTotal = monthly && month ? monthly.totals.find((t) => t.month === month) ?? null : null;

  const partners = useMemo(() => {
    if (!leadData?.partnerRows) return [];
    // Named partners only. A row with no label, an unresolved label or conflicting labels is not a
    // partner name. A known name with no Referrer ID ('missing_id') IS a partner: the server's
    // leadSourceInsights.ts counts it as one, and Leads & Partners lists it.
    return leadData.partnerRows
      .filter((r) => (r.issue === null || r.issue === 'missing_id') && r.deals > 0)
      .sort((a, b) => b.deals - a.deals || a.partnerLabel.localeCompare(b.partnerLabel))
      .slice(0, 5);
  }, [leadData]);

  const bullets = useMemo(() => {
    if (!flowReport?.customPeriod || !month || !prevMonth || !range) return [];
    // Never compared when the previous month could not be read: "no earlier month" would be untrue.
    if (comparable && !prevMonthly) return [];
    // Defence in depth: the previous month's own window must be whole, or it is not compared.
    const prevWhole = prevMonthly?.months?.find((w) => w.month === prevMonth)?.partial !== true;
    const prevTotals = comparable && prevWhole ? prevMonthly?.totals.find((t) => t.month === prevMonth) ?? null : null;
    const prevPoint = prevPack ? points.find((p) => p.asAtDate === prevPack.asAtDate) : undefined;
    const thisPoint = points.find((p) => p.asAtDate === asAtDate);
    return buildPackBullets({
      month,
      monthName: monthLabel(month),
      complete,
      previousMonthName: monthLabel(prevMonth),
      toLabel: formatIsoDate(range.to),
      settled: flowReport.customPeriod.settled,
      submissions: flowReport.customPeriod.lodged,
      previous: prevTotals ? { monthName: monthLabel(prevMonth), settled: prevTotals.settled, submissions: prevTotals.submissions } : null,
      pipeline: thisPoint ? { deals: thisPoint.deals, valueCents: thisPoint.loanAmountCents } : null,
      previousPipeline: prevPoint
        ? { dateLabel: formatIsoDate(prevPoint.asAtDate), deals: prevPoint.deals, valueCents: prevPoint.loanAmountCents }
        : null,
      // The Unassigned bucket is a row in the table (so the rows add up to the Total) but never a
      // broker: it must not be named as the month's top settler.
      brokers: brokerRows.filter((b) => b.kind !== 'unassigned').map((b) => ({ name: b.name, settled: b.settled })),
      topPartner: partners[0] ? { label: partners[0].partnerLabel, leads: partners[0].deals } : null,
    });
  }, [flowReport, month, prevMonth, range, comparable, prevMonthly, prevPack, points, asAtDate, complete, brokerRows, partners]);

  const comparisons = useMemo(() => {
    if (!flowReport?.customPeriod || !month || !prevMonth || !lastYearMonth) return [];
    const thisPoint = points.find((p) => p.asAtDate === asAtDate);
    return buildPackComparisons({
      settled: flowReport.customPeriod.settled,
      submissions: flowReport.customPeriod.lodged,
      previousFlow: flowBaseline({ month: prevMonth, complete, pack: prevPack, response: prevMonthly, error: prevError }),
      lastYearFlow: flowBaseline({ month: lastYearMonth, complete, pack: lyPack, response: lyMonthly, error: lyError }),
      // The Pipeline report's month-end figure, which the old value tile showed; the trend series at
      // the same export when the report has not answered. Both are folded by the same aggregator.
      pipeline: report
        ? { count: report.totals.inFlightDeals, valueCents: report.totals.inFlightValueCents }
        : thisPoint ? { count: thisPoint.deals, valueCents: thisPoint.loanAmountCents } : null,
      previousPipeline: stockBaseline(prevMonth, prevPack, points),
      lastYearPipeline: stockBaseline(lastYearMonth, lyPack, points),
    });
  }, [flowReport, report, month, prevMonth, lastYearMonth, points, asAtDate, complete, prevPack, prevMonthly, prevError, lyPack, lyMonthly, lyError]);
  const sentence = (key: string): string | undefined => bullets.find((b) => b.key === key)?.text;
  const topSettler = topSettlers(
    brokerRows.filter((b) => b.kind !== 'unassigned').map((b) => ({ name: b.name, settled: b.settled })),
    flowReport?.customPeriod?.settled.valueCents ?? 0,
  );

  if (trends.loading && !trends.result) return <SkeletonStatCards />;
  if (trends.error) {
    // The pack's months and its week-on-week pipeline line come from the snapshot history (the
    // Trends report). Say which part failed, rather than repeating another tab's message as though
    // the whole pack were a Trends page.
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
        <AlertCircle className="h-4 w-4" />
        <span>The board pack could not load the snapshot history it builds its months from.</span>
        <button
          type="button"
          onClick={trends.reload}
          className="rounded-md border border-red-400/40 px-2.5 py-1 text-xs font-semibold text-red-100 hover:bg-red-500/10"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!selected || !month || !range) {
    return <Honesty>No Performance export has been published yet, so there is no month to build a pack for.</Honesty>;
  }

  // Newest first, so the earlier month is the next entry and the later month the one before.
  const monthIndex = packMonths.findIndex((m) => m.month === month);
  const earlier = monthIndex >= 0 ? packMonths[monthIndex + 1] : undefined;
  const later = monthIndex > 0 ? packMonths[monthIndex - 1] : undefined;
  const maxBrokerSettled = Math.max(0, ...brokerRows.map((r) => r.settled.valueCents));
  const maxPartnerLeads = Math.max(0, ...partners.map((p) => p.deals));
  const headlineLoading = pipeline.loading || flowLoading
    || (!prevShared && prevMonths.loading) || (!lySharedWith && lyMonths.loading);
  const stockInfo = complete
    ? `${num(report?.totals.inFlightDeals)} deals in flight at month end, as at the ${formatIsoDate(asAtDate)} export.`
    : `${num(report?.totals.inFlightDeals)} deals in flight at ${formatIsoDate(asAtDate)}.`;
  const flowInfo = `Dated ${formatIsoDate(range.from)} to ${formatIsoDate(range.to)}, read from the ${formatIsoDate(flowAsAtDate)} export.`;
  const INFO: Record<PackComparison['key'], string> = {
    settlements: `Deals settled in the month. ${flowInfo}`,
    submissions: `Deals lodged with a lender in the month. ${flowInfo}`,
    pipeline: stockInfo,
  };

  return (
    <div className="space-y-6">
      {/* A month picker, not a period: one pack per calendar month with an export. Styled as the
          PeriodBar's segments so the pack reads as the same product as every other tab. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="pack-month-picker">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Pack month</span>
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-700 bg-gray-900/60 p-0.5">
          <button
            type="button"
            aria-label="Earlier month"
            disabled={!earlier}
            onClick={() => earlier && setChosen(earlier.month)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="relative inline-flex items-center">
            <CalendarDays className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-blue-100" aria-hidden="true" />
            <select
              aria-label="Pack month"
              value={month}
              onChange={(e) => setChosen(e.target.value)}
              className="h-8 cursor-pointer appearance-none rounded-md bg-blue-600 pl-8 pr-3 text-xs font-semibold tracking-wide text-white shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-300"
            >
              {packMonths.map((m) => (
                <option key={m.month} value={m.month} className="bg-gray-900 text-gray-100">{monthLabel(m.month)}</option>
              ))}
            </select>
          </span>
          <button
            type="button"
            aria-label="Later month"
            disabled={!later}
            onClick={() => later && setChosen(later.month)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {complete ? <Chip tone="green">Whole month</Chip> : <Chip tone="amber">{`Month to ${formatIsoDate(range.to)}`}</Chip>}
        <span className="text-xs text-gray-500">Scheduled publishing and share links: planned, not yet built</span>
      </div>

      <TrackerSection
        id="pack-month"
        step={`1 · ${monthLabel(month)}`}
        title="The month"
        description="Settled, submissions and pipeline value against last month and last year, the brokers and referral partners behind them, then the loan book from the commission statements."
      >
      <Panel
        title={`Board pack · ${monthLabel(month)}`}
        icon={Presentation}
        accent={BRAND_BLUE}
        badge={prevMonth && lastYearMonth && <Chip tone="blue">{`Against ${monthLabel(prevMonth)} and ${monthLabel(lastYearMonth)}`}</Chip>}
        blurb={packBlurb({ month, asAtDate: asAtDate ?? '', flowAsAtDate: flowAsAtDate ?? '', complete, range })}
      >
        <div className="space-y-4">
          {pipeline.error && <p className="text-sm text-red-300">{pipeline.error}</p>}
          {flowError && <p className="text-sm text-red-300">{flowError}</p>}
          {prevError && prevMonth && (
            <p className="text-sm text-red-300">{`${monthLabel(prevMonth)} could not be loaded, so ${monthLabel(month)} is not compared with it.`}</p>
          )}
          {lyError && lastYearMonth && (
            <p className="text-sm text-red-300">{`${monthLabel(lastYearMonth)} could not be loaded, so ${monthLabel(month)} is not compared with it.`}</p>
          )}
          {headlineLoading ? (
            <SkeletonStatCards count={3} columns={3} />
          ) : comparisons.length > 0 ? (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label="What changed this month">
              {comparisons.map((c) => (
                <ComparisonCard
                  key={c.key}
                  comparison={c}
                  month={month}
                  color={COMPARE_STYLE[c.key].color}
                  icon={COMPARE_STYLE[c.key].icon}
                  info={INFO[c.key]}
                  sentence={sentence(c.key)}
                />
              ))}
            </ul>
          ) : (pipeline.response && pipeline.response.status !== 'ok') || (flowResponse && flowResponse.status !== 'ok') ? (
            <Honesty>The pipeline figures for this export are not available.</Honesty>
          ) : (
            <p className="text-sm text-gray-400">Waiting for this month&apos;s figures.</p>
          )}

          {!anyLoading && (topSettler || partners[0]) && (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-label="Leaders this month">
              {topSettler && (
                <LeaderCard
                  testId="leader-broker"
                  label={topSettler.names.length === 1 ? 'Top settler' : 'Top settlers, tied'}
                  name={topSettler.names.join(' and ')}
                  value={`${dollars(topSettler.valueCents)} settled${topSettler.names.length > 1 ? ' each' : ''}`}
                  sub={topSettler.sharePercent !== null ? `${topSettler.sharePercent}% of the month's settled value` : undefined}
                  color={CHART_PALETTE[1]}
                  icon={Trophy}
                />
              )}
              {partners[0] && (
                <LeaderCard
                  testId="leader-partner"
                  label="Top referral partner"
                  name={partners[0].partnerLabel}
                  value={`${num(partners[0].deals)} ${partners[0].deals === 1 ? 'lead' : 'leads'} created`}
                  sub={`In ${monthLabel(month)}, ${num(partners[0].settled)} settled so far`}
                  color={CHART_PALETTE[5]}
                  icon={UserPlus}
                />
              )}
            </ul>
          )}
          <p className="text-[0.6875rem] text-gray-500">
            Written from the figures above by fixed rules. Each month is read from its own first export after it ended; a part month is not compared with a whole one. Hover a card for the exact figures.
          </p>
        </div>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <HousePanel
          title="Brokers"
          icon={Users}
          color={CHART_PALETTE[2]}
          badge={brokerRows.length > 0 ? <Chip tone="green">{`${num(brokerRows.length)} with activity`}</Chip> : undefined}
          blurb="Settled and submitted in the month, by broker, largest settled value first. Avg settled deal is settled dollars over settled deals, against each broker's own average deal target where one is set."
          flush
        >
          <div className="p-5 pt-3">
            {months.error && <p className="text-sm text-red-300">{months.error}</p>}
            {months.loading && <SkeletonTable rows={4} columns={4} />}
            {monthly && (brokerRows.length === 0 ? (
              <p className="text-sm text-gray-400">No settlements or submissions in this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700/70 text-left">
                    <th className={TH}>Broker</th>
                    <th className={`${TH} text-right`}>Settled</th>
                    <th className={`${TH} text-right`}>Deals</th>
                    <th className={`${TH} text-right`}>Avg settled deal</th>
                    <th className={`${TH} text-right`}>Submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {brokerRows.map((r) => (
                    <tr key={`${r.kind}:${r.key}`} className={ROW}>
                      <td className="px-2.5 py-2 text-gray-200">
                        {r.name}
                        <RatioBar ratio={maxBrokerSettled > 0 ? r.settled.valueCents / maxBrokerSettled : 0} color={CHART_PALETTE[2]} className="mt-1 w-full max-w-[8rem]" />
                      </td>
                      <td className={`${TD} text-right font-semibold text-emerald-300`}>{formatCents(r.settled.valueCents)}</td>
                      <td className={`${TD} text-right text-gray-200`}>{num(r.settled.count)}</td>
                      <td className={`${TD} text-right text-gray-200`} data-testid="pack-avg-deal">
                        {averageDealCents(r.settled) !== null ? formatCents(averageDealCents(r.settled)) : <span className="text-gray-500">None settled</span>}
                        {/* Unassigned is a bucket, not a broker: it has no target to be graded on. */}
                        {targets && r.kind !== 'unassigned' && (
                          <TargetGradeLine
                            target={averageDealTarget(targets, { adviserId: r.key })}
                            actual={averageDealCents(r.settled)}
                            rag={targets.rag}
                            format={formatCents}
                            ungradedReason="none settled"
                            className="justify-end"
                          />
                        )}
                      </td>
                      <td className={`${TD} text-right text-blue-200`}>{num(r.submissions.count)}</td>
                    </tr>
                  ))}
                  {monthTotal && (
                    <tr className="border-t border-gray-600 font-semibold">
                      <td className="px-2.5 py-2 text-gray-100">Total</td>
                      <td className={`${TD} text-right text-gray-100`}>{formatCents(monthTotal.settled.valueCents)}</td>
                      <td className={`${TD} text-right text-gray-100`}>{num(monthTotal.settled.count)}</td>
                      <td className={`${TD} text-right text-gray-100`} data-testid="pack-avg-deal-total">
                        {averageDealCents(monthTotal.settled) !== null ? formatCents(averageDealCents(monthTotal.settled)) : <span className="text-gray-500">None settled</span>}
                        {targets && (
                          <TargetGradeLine
                            target={averageDealTarget(targets)}
                            actual={averageDealCents(monthTotal.settled)}
                            rag={targets.rag}
                            format={formatCents}
                            ungradedReason="none settled"
                            className="justify-end font-normal"
                          />
                        )}
                      </td>
                      <td className={`${TD} text-right text-gray-100`}>{num(monthTotal.submissions.count)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ))}
          </div>
        </HousePanel>
        <HousePanel
          title="Top referral partners"
          icon={UserPlus}
          color={CHART_PALETTE[5]}
          badge={partners.length > 0 ? <Chip tone="cyan">{`Top ${num(partners.length)}`}</Chip> : undefined}
          blurb="Named referral partners by leads created this month, with how many have settled so far."
          flush
        >
          <div className="p-5 pt-3">
            {leads.error && <p className="text-sm text-red-300">{leads.error}</p>}
            {leads.loading && <SkeletonTable rows={4} columns={3} />}
            {leadResult && leadResult.status !== 'ok' && (
              <p className="text-sm text-gray-400">
                No Sales export matches the {formatIsoDate(flowAsAtDate)} export, so referral partners cannot be shown for this month.
              </p>
            )}
            {leadData && (partners.length === 0 ? (
              <p className="text-sm text-gray-400">No leads from a named referral partner were created this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700/70 text-left">
                    <th className={TH}>Partner</th>
                    <th className={`${TH} text-right`}>Leads</th>
                    <th className={`${TH} text-right`}>Settled so far</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr key={p.key} className={ROW}>
                      <td className="px-2.5 py-2 text-gray-200">
                        {p.partnerLabel}
                        <RatioBar ratio={maxPartnerLeads > 0 ? p.deals / maxPartnerLeads : 0} color={CHART_PALETTE[5]} className="mt-1 w-full max-w-[8rem]" />
                      </td>
                      <td className={`${TD} text-right font-semibold text-cyan-300`}>{num(p.deals)}</td>
                      <td className={`${TD} text-right text-emerald-300`}>{num(p.settled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </HousePanel>
      </div>
      <NetBookPanel companyId={companyId} month={month} targets={targets} />
      </TrackerSection>

      <TrackerSection
        id="pack-outlook"
        step="2 · Outlook"
        title="Next four months, at month end"
        description={`Read at the ${formatIsoDate(asAtDate)} export.`}
      >
        {salesData !== null ? (
          <>
            <ForecastPanel forecast={salesData.forecast} />
            <PlannedSettlementsPanel planned={salesData.planned} />
            {salesData.velocity && <PipelineVelocityPanel velocity={salesData.velocity} />}
          </>
        ) : (
          <SalesAnalyticsState
            title="Pipeline forecast and planned settlements"
            response={sales.response}
            loading={sales.loading}
            error={sales.error}
            onRetry={sales.refetch}
          />
        )}
      </TrackerSection>

      <ComingSoonList items={BOARD_PACK_COMING_SOON} />
    </div>
  );
};

export default BoardPackTab;
